import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Mirrors the Cognito user-pool password policy in infrastructure/auth.tf (minimum_length 8,
 * require_lowercase/uppercase/numbers = true, require_symbols = false). Keep the two in step. */
export const PASSWORD_RULES: { key: 'length' | 'lower' | 'upper' | 'number'; test: (p: string) => boolean }[] = [
  { key: 'length', test: (p) => p.length >= 8 },
  { key: 'lower', test: (p) => /[a-z]/.test(p) },
  { key: 'upper', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', test: (p) => /[0-9]/.test(p) },
];

export function passwordMeetsPolicy(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  /** Show the live requirements checklist (sign-up / new password). */
  showRules?: boolean;
  error?: string;
}

/** Review fix F11: a password input with a show/hide toggle, the visible Cognito requirements,
 * and a field-level error wired through aria-describedby / aria-invalid. */
export function PasswordField({ id, label, value, onChange, autoComplete, showRules, error }: PasswordFieldProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const rulesId = `${id}-rules`;
  const errorId = `${id}-error`;
  const describedBy = [showRules ? rulesId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="login-field">
      <label className="login-label" htmlFor={id}>{label}</label>
      <div className="password-input-row">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="login-input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
        <button
          type="button"
          className="password-toggle"
          aria-controls={id}
          aria-label={visible ? t('auth.hidePasswordLabel') : t('auth.showPasswordLabel')}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? t('auth.hidePassword') : t('auth.showPassword')}
        </button>
      </div>
      {showRules && (
        <div id={rulesId} className="password-rules">
          <p>{t('auth.passwordRulesTitle')}</p>
          <ul>
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(value);
              return (
                <li key={rule.key} className={met ? 'met' : undefined}>
                  <span aria-hidden="true">{met ? '✓' : '○'}</span>
                  {t(`auth.rules.${rule.key}`)}
                  <span className="visually-hidden"> {met ? t('auth.ruleMet') : t('auth.ruleUnmet')}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {error && <p id={errorId} className="field-error">{error}</p>}
    </div>
  );
}
